import React from 'react';
import { Link } from 'react-router-dom';

function SpeakingSkillsGuide({ darkMode }) {
  const containerClass = darkMode 
    ? 'min-h-screen bg-gray-900 text-white' 
    : 'min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 text-gray-800';

  const cardClass = darkMode 
    ? 'bg-gray-800 border-gray-700' 
    : 'bg-white border-gray-200';

  return (
    <div className={containerClass}>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* 헤더 */}
        <div className="text-center mb-12">
          <div className="text-6xl mb-4">🗣️</div>
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
            목소리 & 말하기 기술
          </h1>
          <p className="text-xl text-gray-600 mb-6">
            발음, 속도, 톤 조절로 더 매력적으로 말하기
          </p>
          <div className={`inline-block px-6 py-3 rounded-full text-sm font-semibold ${darkMode ? 'bg-indigo-900 text-indigo-200' : 'bg-indigo-100 text-indigo-800'}`}>
            💡 아무리 좋은 답변도 제대로 전달되지 않으면 의미가 없습니다
          </div>
        </div>

        {/* 중요성 섹션 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-indigo-600">🎯 면접에서 목소리가 결정적인 이유</h2>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="text-2xl">📢</div>
              <p className="text-lg">면접관은 내용과 동시에 말하는 방식으로 첫인상 판단</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="text-2xl">🔇</div>
              <p className="text-lg">목소리가 작거나 산만하면 좋은 내용도 묻혀버림</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="text-2xl">👥</div>
              <p className="text-lg">처음 듣는 사람도 편안하게 들을 수 있어야 설득력 증가</p>
            </div>
          </div>
        </div>

        {/* 자가 진단 섹션 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-red-600">🔍 내 목소리 진단하기 (필수!)</h2>
          
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4 text-red-500">📱 자가 진단 3단계</h3>
            
            <div className="space-y-6">
              <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-red-50'}`}>
                <h4 className="font-bold mb-3 text-red-600">1단계: 녹음해서 객관적으로 듣기</h4>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} border-l-4 border-red-500`}>
                  <p className="font-semibold mb-2">연습 문장:</p>
                  <p className="italic">"안녕하십니까. 저는 마케팅 직무에 지원한 김○○입니다. 저의 가장 큰 강점은 소통 능력입니다."</p>
                </div>
                <p className="mt-3 text-sm font-semibold text-red-600">녹음 후 체크할 점:</p>
                <ul className="text-sm mt-2 space-y-1">
                  <li>• 내 목소리가 어떻게 들리는가?</li>
                  <li>• 면접관 입장에서 듣기 편한가?</li>
                  <li>• 자신감 있게 들리는가?</li>
                </ul>
              </div>

              <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-red-50'}`}>
                <h4 className="font-bold mb-3 text-red-600">2단계: 문제점 체크리스트</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="rounded" />
                      <span>말이 너무 빠른가?</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="rounded" />
                      <span>말끝을 흐리는가?</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="rounded" />
                      <span>톤이 너무 낮은가?</span>
                    </label>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="rounded" />
                      <span>목소리가 너무 작은가?</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="rounded" />
                      <span>웅얼거리거나 뭉개는가?</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="rounded" />
                      <span>단조롭고 감정이 없는가?</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-red-50'}`}>
                <h4 className="font-bold mb-3 text-red-600">3단계: 타인 피드백 받기</h4>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} border-l-4 border-red-500`}>
                  <p className="italic mb-3">"내가 처음 만나는 사람이라고 생각하고, 이 자기소개를 들어봐. 어떤 느낌이야?"</p>
                  <div className="space-y-2">
                    <p><span className="text-green-600 font-bold">✅ 긍정적:</span> "또렷하고 자신감 있어 보여"</p>
                    <p><span className="text-red-600 font-bold">❌ 부정적:</span> "너무 빨라", "잘 안 들려", "지루해"</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 문제별 해결법 섹션 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-orange-600">🎯 문제별 맞춤 해결법</h2>
          
          <div className="space-y-8">
            {/* 말이 빠른 경우 */}
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-orange-50'}`}>
              <h3 className="text-xl font-bold mb-4 text-orange-600">⚡ 말이 빠른 경우</h3>
              <div className="space-y-4">
                <div>
                  <p className="font-semibold text-red-600 mb-2">❌ 문제점:</p>
                  <ul className="text-sm space-y-1 ml-4">
                    <li>• 면접관이 따라가기 어려움</li>
                    <li>• 긴장되거나 불안해 보임</li>
                    <li>• 내 경험을 설명할 때 특히 치명적</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-green-600 mb-2">✅ 해결법:</p>
                  <ul className="text-sm space-y-1 ml-4">
                    <li>• <strong>점 찍기 연습:</strong> 문장 끝마다 마음속으로 "점" 하고 쉬기</li>
                    <li>• <strong>숫자 세기:</strong> "안녕하십니까(1) 저는(1) 김○○입니다(1)"</li>
                    <li>• <strong>핵심 단어 강조:</strong> 중요한 단어는 더욱 천천히</li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} border-l-4 border-orange-500`}>
                  <p className="font-bold mb-2">💪 연습법:</p>
                  <p className="text-red-600 text-sm mb-2">❌ 빠른 버전: "저는대학시절마케팅동아리활동을통해서리더십을기를수있었습니다"</p>
                  <p className="text-green-600 text-sm">✅ 개선 버전: "저는. 대학 시절. 마케팅 동아리 활동을 통해서.리더십을.기를 수 있었습니다"</p>
                </div>
              </div>
            </div>

            {/* 말끝을 흐리는 경우 */}
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
              <h3 className="text-xl font-bold mb-4 text-blue-600">🌊 말끝을 흐리는 경우</h3>
              <div className="space-y-4">
                <div>
                  <p className="font-semibold text-red-600 mb-2">❌ 문제점:</p>
                  <ul className="text-sm space-y-1 ml-4">
                    <li>• 확신 없어 보임</li>
                    <li>• 전문성 의심받음</li>
                    <li>• 마지막 인상이 흐려짐</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-green-600 mb-2">✅ 해결법:</p>
                  <ul className="text-sm space-y-1 ml-4">
                    <li>• <strong>끝음절 강화:</strong> "합니다" "습니다"를 또렷하게</li>
                    <li>• <strong>입 모양 의식:</strong> 마지막 글자까지 입 모양 정확히</li>
                    <li>• <strong>녹음 체크:</strong> 끝부분만 반복 연습</li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} border-l-4 border-blue-500`}>
                  <p className="font-bold mb-2">💪 연습법:</p>
                  <p className="text-red-600 text-sm mb-2">❌ 흐린 버전: "최선을 다하겠습니다아~"</p>
                  <p className="text-green-600 text-sm">✅ 개선 버전: "최선을 다하겠습니다!" (끝까지 명확하게)</p>
                </div>
              </div>
            </div>

            {/* 톤이 낮은 경우 */}
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
              <h3 className="text-xl font-bold mb-4 text-purple-600">📉 톤이 낮은 경우</h3>
              <div className="space-y-4">
                <div>
                  <p className="font-semibold text-red-600 mb-2">❌ 문제점:</p>
                  <ul className="text-sm space-y-1 ml-4">
                    <li>• 의욕 없어 보임</li>
                    <li>• 소극적인 인상</li>
                    <li>• 나이 들어 보임</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-green-600 mb-2">✅ 해결법:</p>
                  <ul className="text-sm space-y-1 ml-4">
                    <li>• <strong>평소보다 한 음 높이기:</strong> 부자연스럽지 않을 정도로</li>
                    <li>• <strong>미소 지으며 말하기:</strong> 자연스럽게 톤이 밝아짐</li>
                    <li>• <strong>첫 문장 특히 신경쓰기:</strong> 첫인상이 가장 중요</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 목소리가 작은 경우 */}
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
              <h3 className="text-xl font-bold mb-4 text-green-600">🔇 목소리가 작은 경우</h3>
              <div className="space-y-4">
                <div>
                  <p className="font-semibold text-red-600 mb-2">❌ 문제점:</p>
                  <ul className="text-sm space-y-1 ml-4">
                    <li>• "예?" 하고 되묻게 만듬</li>
                    <li>• 소극적이고 자신감 없어 보임</li>
                    <li>• 면접관이 집중하기 어려움</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-green-600 mb-2">✅ 해결법:</p>
                  <ul className="text-sm space-y-1 ml-4">
                    <li>• <strong>횡격막 호흡:</strong> 가슴이 아닌 배로 숨쉬기</li>
                    <li>• <strong>거리감 연습:</strong> 3미터 떨어진 사람에게 말한다고 생각</li>
                    <li>• <strong>첫 단어 크게:</strong> 시작을 크게 하면 자연스럽게 유지됨</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 긍정적 인상 주는 법 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-green-600">✨ 처음 듣는 사람도 긍정적 인상을 주는 법</h2>
          
          <div className={`p-6 rounded-xl mb-6 ${darkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
            <h3 className="text-xl font-bold mb-4 text-green-600">🎯 황금 법칙: 면접관 관점에서 생각하기</h3>
            <p className="mb-4 font-semibold">면접관은 여러분을 처음 만나는 사람입니다</p>
            <ul className="space-y-2">
              <li>• 여러분의 성격, 경험, 능력을 전혀 모름</li>
              <li>• 짧은 시간에 모든 것을 판단해야 함</li>
              <li>• 목소리로 첫인상이 결정됨</li>
            </ul>
          </div>

          <div className="space-y-6">
            <div>
              <h4 className="font-bold mb-3 text-green-600">📢 긍정적 인상을 주는 목소리 특징</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-green-500`}>
                  <h5 className="font-bold mb-2">또박또박 큰 소리</h5>
                  <ul className="text-sm space-y-1">
                    <li>• 면접관 연령대(40-50대) 고려</li>
                    <li>• 자신감과 적극성 어필</li>
                    <li>• 중요한 내용이 묻히지 않음</li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-green-500`}>
                  <h5 className="font-bold mb-2">적절한 속도와 쉼</h5>
                  <ul className="text-sm space-y-1">
                    <li>• 내 경험을 처음 듣는 사람도 이해</li>
                    <li>• 여유 있고 안정된 인상</li>
                    <li>• 신뢰감 증가</li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-green-500`}>
                  <h5 className="font-bold mb-2">일정한 톤 유지</h5>
                  <ul className="text-sm space-y-1">
                    <li>• 감정 기복 없이 안정적</li>
                    <li>• 전문적이고 성숙한 느낌</li>
                    <li>• 업무 능력 신뢰도 상승</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-3 text-green-600">🚀 상황별 목소리 전략</h4>
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-green-500`}>
                  <h5 className="font-bold mb-2">자기소개할 때</h5>
                  <p className="text-red-600 text-sm mb-1">❌ "안녕하세요, 저는... 음... 김○○이고요..."</p>
                  <p className="text-green-600 text-sm">✅ "안녕하십니까! 저는 마케팅 직무에 지원한 김○○입니다!"</p>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-green-500`}>
                  <h5 className="font-bold mb-2">경험을 설명할 때</h5>
                  <p className="text-red-600 text-sm mb-1">❌ "프로젝트를했는데성공했어요" (빠르고 뭉개짐)</p>
                  <p className="text-green-600 text-sm">✅ "프로젝트를 진행했는데... 성공적으로 완료했습니다" (명확하고 구체적)</p>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-green-500`}>
                  <h5 className="font-bold mb-2">마무리할 때</h5>
                  <p className="text-red-600 text-sm mb-1">❌ "감사합니다아~" (끝을 흐림)</p>
                  <p className="text-green-600 text-sm">✅ "감사합니다!" (또렷하고 확신 있게)</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 연습법 섹션 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-purple-600">🏋️ 체계적 연습법</h2>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-bold mb-4 text-purple-600">📅 일주일 집중 훈련 프로그램</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
                  <h4 className="font-bold mb-3 text-purple-600">1-2일차: 진단 및 인식</h4>
                  <ul className="text-sm space-y-2">
                    <li>• 매일 5분씩 녹음하며 문제점 파악</li>
                    <li>• 가족/친구에게 피드백 요청</li>
                    <li>• 가장 큰 문제점 1-2개 선정</li>
                  </ul>
                </div>
                <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
                  <h4 className="font-bold mb-3 text-purple-600">3-4일차: 집중 개선</h4>
                  <ul className="text-sm space-y-2">
                    <li>• 선정된 문제점만 집중 연습</li>
                    <li>• 같은 문장 10번 반복하며 개선</li>
                    <li>• 개선 전후 녹음 비교</li>
                  </ul>
                </div>
                <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
                  <h4 className="font-bold mb-3 text-purple-600">5-6일차: 통합 연습</h4>
                  <ul className="text-sm space-y-2">
                    <li>• 전체 자기소개를 완전한 목소리로 연습</li>
                    <li>• 다양한 면접 질문에 적용</li>
                    <li>• 실전처럼 연습 (의자에 앉아서, 정장 입고)</li>
                  </ul>
                </div>
                <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
                  <h4 className="font-bold mb-3 text-purple-600">7일차: 최종 점검</h4>
                  <ul className="text-sm space-y-2">
                    <li>• 처음 녹음과 마지막 녹음 비교</li>
                    <li>• 타인에게 최종 피드백 요청</li>
                    <li>• 부족한 부분 마지막 보완</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-4 text-purple-600">🪞 매일 5분 거울 연습</h3>
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-purple-500`}>
                  <h4 className="font-bold mb-2">Step 1: 표정 + 목소리 동시 체크</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 거울 보며 밝은 표정으로 말하기</li>
                    <li>• 표정이 목소리에 미치는 영향 확인</li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-purple-500`}>
                  <h4 className="font-bold mb-2">Step 2: 입 모양 정확성 체크</h4>
                  <ul className="text-sm space-y-1">
                    <li>• 자음, 모음 구분되는지 확인</li>
                    <li>• 특히 마지막 음절까지 명확히</li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} border-l-4 border-purple-500`}>
                  <h4 className="font-bold mb-2">Step 3: 전체적인 인상 체크</h4>
                  <ul className="text-sm space-y-1">
                    <li>• "저 사람과 함께 일하고 싶을까?" 자문</li>
                    <li>• 긍정적 인상인지 객관적 평가</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 면접 당일 관리 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-blue-600">🎯 면접 당일 목소리 관리</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
              <h3 className="font-bold mb-4 text-blue-600">⏰ 면접 2시간 전</h3>
              <div className="space-y-3">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">따뜻한 물 마시기</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">목과 어깨 스트레칭</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">발성 연습 5분</span>
                </label>
              </div>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
              <h3 className="font-bold mb-4 text-blue-600">⏰ 면접 30분 전</h3>
              <div className="space-y-3">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">자기소개 1회 연습</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">목소리 크기 테스트</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">심호흡으로 긴장 완화</span>
                </label>
              </div>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
              <h3 className="font-bold mb-4 text-blue-600">⏰ 면접장 입장 직전</h3>
              <div className="space-y-3">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">마지막 목소리 점검</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">"또박또박, 크고 명확하게" 다짐</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">자신감 있는 미소 준비</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* 최종 체크리스트 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-indigo-600">💡 최종 체크리스트</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-bold mb-4 text-indigo-600">✅ 내 목소리가 준비되었는지 확인</h3>
              <div className="space-y-3">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span>녹음해서 들어봤을 때 긍정적인가?</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span>처음 듣는 사람도 편안하게 들을 수 있는가?</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span>면접관에게 좋은 첫인상을 줄 수 있는가?</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span>말이 빠르거나 흐리지 않는가?</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span>톤이 적절하고 목소리가 충분히 큰가?</span>
                </label>
              </div>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-indigo-50'}`}>
              <h3 className="text-xl font-bold mb-4 text-indigo-600">🚀 핵심 기억 포인트</h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="text-2xl">1️⃣</div>
                  <div>
                    <p className="font-bold">또박또박 큰 소리</p>
                    <p className="text-sm text-gray-600">면접관이 편안하게 들을 수 있게</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="text-2xl">2️⃣</div>
                  <div>
                    <p className="font-bold">처음 듣는 사람 관점</p>
                    <p className="text-sm text-gray-600">내 경험도 이해하기 쉽게</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="text-2xl">3️⃣</div>
                  <div>
                    <p className="font-bold">전달력이 생명</p>
                    <p className="text-sm text-gray-600">좋은 내용도 잘 전달되어야 의미 있음</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 마무리 메시지 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg text-center`}>
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold mb-4 text-indigo-600">마무리 메시지</h2>
          <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-indigo-50'} mb-6`}>
            <p className="text-xl font-bold mb-4 text-indigo-600">
              "좋은 답변 내용 + 좋은 목소리 = 완벽한 면접!"
            </p>
            <p className="mb-4">
              여러분의 훌륭한 내용이 면접관에게 제대로 전달될 수 있도록,<br />
              목소리부터 차근차근 준비해보세요!
            </p>
            <p className="font-semibold text-indigo-600">
              연습할수록 자신감이 생기고, 자신감이 생길수록 목소리가 더 좋아집니다! 🎯
            </p>
          </div>

          {/* 연습 포인트 */}
          <div className={`p-4 rounded-xl ${darkMode ? 'bg-indigo-900' : 'bg-indigo-100'} mb-6`}>
            <p className="text-sm font-semibold text-indigo-600">연습 포인트:</p>
            <ul className="text-sm mt-2 space-y-1">
              <li>• 매일 5분씩 거울 보며 연습하기</li>
              <li>• 녹음해서 객관적으로 내 목소리 점검하기</li>
              <li>• 처음 듣는 사람 입장에서 생각하며 말하기</li>
              <li>• 또박또박 큰 소리로 자신감 있게!</li>
            </ul>
          </div>
        </div>

        {/* 네비게이션 버튼 */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link 
            to="/guide-success" 
            className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 text-center ${
              darkMode 
                ? 'bg-gray-700 text-white hover:bg-gray-600' 
                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
            }`}
          >
            ← 면접성공가이드 메인으로
          </Link>
          <Link 
            to="/" 
            className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl font-semibold hover:from-indigo-700 hover:to-blue-700 transition-all duration-300 text-center"
          >
            🏠 홈으로 돌아가기
          </Link>
        </div>
      </div>
    </div>
  );
}

export default SpeakingSkillsGuide;